import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import * as logging from 'util/Logging';

import {GetTableDataFn} from './extractTableDataForSuggestions';
import {PlotSuggestion} from './SuggestPlotsConstants';
import {suggestPlotsForTable} from './SuggestPlotsDAO';

export interface SuggestPlotsState {
  isLoading: boolean;
  suggestions: Array<PlotSuggestion>;
  errorMessage: string | null;
}

export const DEFAULT_SUGGEST_PLOTS_STATE: SuggestPlotsState = {
  isLoading: false,
  suggestions: [],
  errorMessage: null,
};

export const suggestPlotsGlobalState =
  makeUseGlobalClientState<SuggestPlotsState>(DEFAULT_SUGGEST_PLOTS_STATE);

export function getSuggestPlotsCacheKey(entryId: string, tableName: string): string {
  return `${entryId}::${tableName}`;
}

export interface PrefetchSuggestPlotsParams {
  entryId: string;
  tableName: string;
  getTableData: GetTableDataFn;
}

export async function fetchAndStoreSuggestPlots({
  entryId,
  tableName,
  getTableData,
}: PrefetchSuggestPlotsParams): Promise<void> {
  const cacheKey = getSuggestPlotsCacheKey(entryId, tableName);
  const {columns, rows} = getTableData();
  if (rows.length < 3) {
    suggestPlotsGlobalState.updateState(cacheKey, {
      isLoading: false,
      suggestions: [],
      errorMessage: 'Add more data to get plot suggestions.',
    });
    return;
  }

  suggestPlotsGlobalState.updateState(cacheKey, {
    isLoading: true,
    suggestions: [],
    errorMessage: null,
  });

  try {
    const response = await suggestPlotsForTable({tableName, columns, rows});
    if (response.hadError || response.suggestions.length === 0) {
      suggestPlotsGlobalState.updateState(cacheKey, {
        isLoading: false,
        suggestions: [],
        errorMessage: response.errorMessage ?? 'No suggestions for this table. Try the Custom option below.',
      });
    } else {
      suggestPlotsGlobalState.updateState(cacheKey, {
        isLoading: false,
        suggestions: response.suggestions,
        errorMessage: null,
      });
    }
  } catch (err) {
    logging.error(err, {extra: {message: 'fetchAndStoreSuggestPlots failed'}});
    suggestPlotsGlobalState.updateState(cacheKey, {
      isLoading: false,
      suggestions: [],
      errorMessage: 'An error occurred while trying to generate plot suggestions.',
    });
    throw err;
  }
}

// Cache is keyed by entryId+tableName only (not table contents). Stale suggestions after
// edits are acceptable for v1; a content hash or TTL can be added if this becomes an issue.
export function prefetchSuggestPlots(params: PrefetchSuggestPlotsParams): void {
  const cacheKey = getSuggestPlotsCacheKey(params.entryId, params.tableName);
  const currentState = suggestPlotsGlobalState.getState(cacheKey);
  if (currentState.isLoading || currentState.suggestions.length > 0) {
    return;
  }
  fetchAndStoreSuggestPlots(params).catch(() => undefined);
}
